import React from 'react';
import { FloatingPanel } from '../ui';
import { CreditTransaction, CreditTransactionType, OrgCredits } from '../../client/creditsClient';
import { CreditRequestState } from '../../hooks/useCreditRequest';
import { formatCredits } from '../../utils/credits';
import CreditRequestOutcome from '../credits/CreditRequestOutcome';
import CreditRequestTrigger from '../credits/CreditRequestTrigger';

/**
 * Recent-ledger popover behind the sidebar credits chip. Read-only history —
 * grants and admin surfaces live elsewhere — plus "Request more credits" (or
 * the notice that one is already waiting) when the caller supplies its state.
 * Neutral by design: credits are an account fact, not an AI surface, so no
 * violet appears here.
 */

/** DEBIT takes credits away; everything else puts them back. */
const isDebit = (type: CreditTransactionType) => type === 'DEBIT';

/** `SUPPLIER_RESEARCH` → `Supplier research`; falls back to the ledger type. */
const rowLabel = (tx: CreditTransaction): string => {
  const raw = tx.useCase ?? tx.type;
  const words = raw.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatAmount = (tx: CreditTransaction): string =>
  `${isDebit(tx.type) ? '−' : '+'}${formatCredits(tx.amount)}`;

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export interface CreditsPopoverProps {
  credits: OrgCredits;
  /** `useCreditRequest()`'s state; without it the popover is history only. */
  creditRequest?: CreditRequestState;
  className?: string;
}

export const CreditsPopover: React.FC<CreditsPopoverProps> = ({ credits, creditRequest, className = '' }) => (
  <FloatingPanel className={`w-[286px] ${className}`}>
    <div className="border-b border-line-divider px-s5 py-s4">
      <p className="text-meta text-ink-muted">Organisation balance</p>
      <p className="mt-s1 font-mono text-[17px] font-semibold text-ink">
        {formatCredits(credits.balance)}
      </p>
    </div>
    <div className="px-s5 py-s4">
      <p className="mb-s3 text-table-header uppercase text-ink-muted">Recent activity</p>
      {credits.transactions.length === 0 ? (
        <p className="text-body-sm text-ink-muted">No credit activity yet.</p>
      ) : (
        <ul className="flex flex-col gap-s3">
          {credits.transactions.map((tx) => (
            <li key={tx.id} className="flex items-baseline gap-s3">
              <span className="min-w-0 flex-1 truncate text-body-sm text-ink-body">{rowLabel(tx)}</span>
              <span className="shrink-0 text-meta text-ink-muted">{formatDate(tx.createdAt)}</span>
              <span
                className={`w-[52px] shrink-0 text-right font-mono text-mono-data ${
                  isDebit(tx.type) ? 'text-ink-secondary' : 'text-success-ink'
                }`}
              >
                {formatAmount(tx)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
    {creditRequest && (
      <div className="flex flex-col items-start gap-s3 border-t border-line-divider px-s5 py-s4">
        <CreditRequestTrigger state={creditRequest} onRequest={() => creditRequest.request()} size="sm" />
        <CreditRequestOutcome state={creditRequest} />
      </div>
    )}
  </FloatingPanel>
);

export default CreditsPopover;
